import { useState } from "react";
import { Shield, Loader2, AlertCircle, PenLine } from "lucide-react";
import { api } from "@/lib/api";

const PRIVACY_RIGHTS = [
    "Your health information is protected under federal law (HIPAA).",
    "You have the right to see and get a copy of your health records.",
    "You have the right to ask for corrections to your records.",
    "You have the right to know who has seen your health information.",
    "Your information will only be shared for treatment, payment, healthcare operations, or as you authorize.",
];

const CONFIDENTIALITY_LIMITS = [
    "If you express intent to harm yourself, we must act to keep you safe.",
    "If you express intent to harm someone else, we are required to report it.",
    "Suspected abuse or neglect of children, elders, or dependent adults must be reported.",
    "Records may be released in response to a valid court order.",
    "Facility safety and security concerns may require limited disclosure to correctional staff.",
];

const inputCls = "w-full px-3.5 py-2.5 bg-white border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30";

export const HIPAAWaiverForm = ({ onComplete }) => {
    const [ackPrivacy, setAckPrivacy] = useState(false);
    const [ackLimits, setAckLimits] = useState(false);
    const [signature, setSignature] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        setError("");
        if (!ackPrivacy || !ackLimits) return setError("Please acknowledge both statements.");
        if (signature.trim().length < 2) return setError("Type your full legal name to sign.");
        setSaving(true);
        try {
            await api.post("/onboarding/hipaa-waiver", {
                signature: signature.trim(),
                ack_privacy: true,
                ack_limitations: true,
            });
            onComplete?.();
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not save the acknowledgment.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="hipaa-waiver-form" className="space-y-5">
            <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-2xl bg-phoenix-navy text-white flex items-center justify-center shrink-0">
                    <Shield className="w-5 h-5" />
                </span>
                <div>
                    <h2 className="font-display font-bold text-lg text-phoenix-navy">HIPAA Privacy Acknowledgment</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        Your privacy rights — and the limits of confidentiality in a correctional setting.
                    </p>
                </div>
            </div>

            {error && (
                <div data-testid="hipaa-error" className="flex items-center gap-2 px-3.5 py-2.5 bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-xl">
                    <AlertCircle className="w-4 h-4 shrink-0" /> {error}
                </div>
            )}

            <div className="p-4 bg-phoenix-soft/40 border border-phoenix-navy/15 rounded-2xl">
                <h3 className="text-xs font-bold text-phoenix-navy uppercase tracking-wider mb-2">Your privacy rights</h3>
                <ul className="space-y-1.5">
                    {PRIVACY_RIGHTS.map((r, i) => (
                        <li key={i} className="text-xs text-muted-foreground leading-relaxed flex gap-2">
                            <span className="text-phoenix-navy">•</span> {r}
                        </li>
                    ))}
                </ul>
                <label className="flex items-start gap-2.5 text-sm cursor-pointer mt-3 pt-3 border-t border-phoenix-navy/10">
                    <input data-testid="hipaa-ack-privacy" type="checkbox" className="rounded mt-0.5"
                        checked={ackPrivacy} onChange={(e) => setAckPrivacy(e.target.checked)} />
                    <span className="text-muted-foreground">I have read and understand my privacy rights.</span>
                </label>
            </div>

            <div className="p-4 bg-amber-50/70 border border-amber-200 rounded-2xl">
                <h3 className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-2">Limits of confidentiality</h3>
                <ul className="space-y-1.5">
                    {CONFIDENTIALITY_LIMITS.map((r, i) => (
                        <li key={i} className="text-xs text-muted-foreground leading-relaxed flex gap-2">
                            <span className="text-amber-700">•</span> {r}
                        </li>
                    ))}
                </ul>
                <label className="flex items-start gap-2.5 text-sm cursor-pointer mt-3 pt-3 border-t border-amber-200">
                    <input data-testid="hipaa-ack-limits" type="checkbox" className="rounded mt-0.5"
                        checked={ackLimits} onChange={(e) => setAckLimits(e.target.checked)} />
                    <span className="text-muted-foreground">I understand the limits of confidentiality described above.</span>
                </label>
            </div>

            <div>
                <label className="block text-xs font-semibold text-phoenix-navy uppercase tracking-wider mb-1.5">
                    Type your full legal name to sign
                </label>
                <input data-testid="hipaa-signature-input" type="text" className={inputCls} placeholder="Your full name"
                    value={signature} onChange={(e) => setSignature(e.target.value)} />
            </div>

            <button onClick={submit} disabled={saving} data-testid="hipaa-submit-btn"
                className="w-full py-3 bg-phoenix-navy text-white font-semibold rounded-xl hover:bg-phoenix-navy-2 transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
                Sign acknowledgment
            </button>
        </div>
    );
};
